import { PlayerPicture } from "./PlayerPicture.tsx";
import { SafeHtml } from "./SafeHtml.tsx";
import clsx from "clsx";
import { helpers } from "../util/helpers.ts";
import type { View, LogEventType } from "../../../../common/types.ts";
import { categories, types } from "../util/transactionInfo.ts";
import { translateTeamRegion, translateTeamShortName } from "../util/teamNames.ts";
import { translateEventText } from "../util/eventText.ts";
import i18n from "../util/i18n.ts";
import { createMobileReturnUrl } from "../util/mobilePageContext.ts";
import {
  getNewsCategory,
  getNewsImpactKind,
  getNewsImportanceTier,
  getNewsRelevanceKind,
} from "../util/newsRelevance.ts";

const Badge = ({ type }: { type: LogEventType }) => {
  let text;
  let className;
  const typeInfo = types[type];
  if (typeInfo) {
    text = typeInfo.text;
    className = categories[typeInfo.category].className;
  } else {
    text = type;
    className = "bg-secondary";
  }
  return <span className={`badge badge-news mx-2 ms-auto ${className}`}>{text}</span>;
};

const logoStyle = { maxHeight: 28, maxWidth: 28 };
const topStyle = { height: 36 };
const getMobileScrollY = () => (typeof window !== "undefined" ? window.scrollY : undefined);

const createNewsObjectHref = (url: string, filterValue: string) =>
  createMobileReturnUrl(url, {
    activeTab: "news",
    filterKey: "newsObject",
    filterValue,
    originSection: "league",
    pageID: "news",
    returnLabel: i18n.t("news.returnToNews", {
      ns: "ui",
      defaultValue: "Return to news",
    }),
    scrollY: getMobileScrollY(),
  });

export const NewsBlock = ({
  event,
  season,
  userTid,
  teams,
}: {
  event: View<"news">["events"][number];
  season: number;
  userTid: number;
  teams: View<"news">["teams"];
}) => {
  let teamName = null;
  let teamInfo;
  if (event.tid !== undefined) {
    teamInfo = teams[event.tid];

    if (teamInfo) {
      const rosterURL = createNewsObjectHref(
        helpers.leagueUrl(["roster", `${teamInfo.abbrev}_${event.tid}`, season]),
        `team:${event.tid}:season:${season}`,
      );

      teamName = (
        <>
          {teamInfo.imgURL || teamInfo.imgURLSmall ? (
            <a className="p-1" href={rosterURL}>
              <img src={teamInfo.imgURLSmall ?? teamInfo.imgURL} alt="" style={logoStyle} />
            </a>
          ) : null}
          <a className="ps-1" href={rosterURL}>
            {translateTeamRegion(teamInfo.region)}
          </a>
        </>
      );
    }
  } else if (event.tids && event.tids.length <= 3) {
    // Show multiple logos, like for a trade;
    teamName = event.tids.map((tid) => {
      teamInfo = teams[tid];

      if (!teamInfo) {
        return null;
      }
      const rosterURL = createNewsObjectHref(
        helpers.leagueUrl(["roster", `${teamInfo.abbrev}_${tid}`, season]),
        `team:${tid}:season:${season}`,
      );

      return (
        <a className="p-1" href={rosterURL} key={tid}>
          {teamInfo.imgURL || teamInfo.imgURLSmall ? (
            <img src={teamInfo.imgURLSmall ?? teamInfo.imgURL} alt="" style={logoStyle} />
          ) : (
            translateTeamShortName(teamInfo, teamInfo.abbrev)
          )}
        </a>
      );
    });
  }
  const relevanceKind = getNewsRelevanceKind(event, userTid);
  const importanceTier = getNewsImportanceTier(event);
  const impactKind = getNewsImpactKind(event);
  const category = getNewsCategory(event);
  const impactLabel = (() => {
    switch (impactKind) {
      case "draft":
        return i18n.t("news.impactDraft", { ns: "ui", defaultValue: "Draft context" });
      case "market":
        return i18n.t("news.impactMarket", { ns: "ui", defaultValue: "Market impact" });
      case "roster":
        return i18n.t("news.impactRoster", { ns: "ui", defaultValue: "Roster impact" });
      case "standings":
        return i18n.t("news.impactStandings", { ns: "ui", defaultValue: "Standings impact" });
      case "star":
        return i18n.t("news.impactStar", { ns: "ui", defaultValue: "Star/player impact" });
      default:
        return i18n.t("news.impactLeague", { ns: "ui", defaultValue: "League impact" });
    }
  })();
  const objectID =
    event.tid !== undefined
      ? `team:${event.tid}`
      : event.tids && event.tids.length > 0
        ? `teams:${event.tids.join(",")}`
        : event.pids && event.pids.length > 0
          ? `players:${event.pids.join(",")}`
          : "league";

  return (
    <div
      className={clsx("card", "mobile-list-card", {
        "opacity-75": importanceTier === "ordinary",
      })}
      data-importance-tier={importanceTier}
      data-mobile-list-card=""
      data-mobile-list-card-tone={importanceTier === "high" ? "warning" : "neutral"}
      data-news-event={event.type}
      data-news-event-eid={event.eid}
      data-news-impact={importanceTier}
      data-news-impact-kind={impactKind}
      data-news-impact-label={impactLabel}
      data-news-object={objectID}
      data-news-category={category}
      data-ordinary-news-lower-weight={importanceTier === "ordinary" ? "" : undefined}
      data-relevance-score={relevanceKind}
      role="listitem"
    >
      <div
        className={clsx(
          "d-flex align-items-center",
          event.tids && event.tids.includes(userTid) ? "table-info" : "card-header p-0",
        )}
        style={topStyle}
      >
        {teamName}
        <Badge type={event.type} />
      </div>
      <div className="d-flex">
        {event.p && event.p.imgURL !== "/img/blank-face.png" ? (
          <div
            style={{
              height: 90,
              width: 60,
              marginTop: event.p.imgURL ? 0 : -10,
            }}
            className="flex-shrink-0"
          >
            <PlayerPicture
              face={event.p.face}
              imgURL={event.p.imgURL}
              colors={teamInfo?.colors}
              jersey={teamInfo?.jersey}
              lazy
            />
          </div>
        ) : null}
        <div className="p-2">
          <div data-localized-event-text="">
            <SafeHtml dirty={translateEventText(event)} />
          </div>
        </div>
      </div>
    </div>
  );
};
